// E:\new-react-project\the-ultimate-guide\src\core-pages\Login.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../Security/firebase-config";
import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  sendPasswordResetEmail,
  signInWithCustomToken,
  fetchSignInMethodsForEmail,
  setPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

import { createUserRecord } from "../utils/createUserRecord";

import "./Login.css";
import CustomAlert from "../components/core-components/CustomAlert";

import { setDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "../Security/firebase-config";

// Local provider so we can force the chooser + control redirect behavior
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

const REVIEWER_EMAIL = "contact.admin@valovate.com";
const REVIEWER_PASSWORD =
  process.env.REACT_APP_RIOT_REVIEWER_PASSWORD || "Valovate123!";

// Detect in-app browsers (Discord/Instagram/etc) that often break popups
const isInAppBrowser = () =>
  /FBAN|FBAV|Instagram|Line|Discord|Twitter|LinkedIn|Snapchat/i.test(navigator.userAgent);

// Prefer redirect on real mobile browsers because popup handling is inconsistent there.
const isMobileBrowser = () => {
  if (typeof navigator === "undefined") {
    return false;
  }

  return /Android|iPhone|iPad|iPod|IEMobile|Opera Mini|Mobile/i.test(
    navigator.userAgent || ""
  );
};

// iOS Safari and in-app browsers often struggle with popup auth flows.
const shouldUseRedirect = () => isInAppBrowser() || isMobileBrowser();

function makeTempUsername() {
  const digits = String(Math.floor(Math.random() * 10000)).padStart(4, "0");
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const a = letters[Math.floor(Math.random() * letters.length)];
  const b = letters[Math.floor(Math.random() * letters.length)];
  return `user${digits}${a}${b}`;
}

async function reserveUsername(db, uid, username) {
  const unameLower = username.toLowerCase();

  // 1) usernames/{unameLower}
  await setDoc(doc(db, "usernames", unameLower), {
    uid,
    username,
    createdAt: serverTimestamp(),
  });

  // 2) uids/{uid}
  await setDoc(doc(db, "uids", uid), {
    uid,
    username,
    unameLower,
    createdAt: serverTimestamp(),
  });

  return unameLower;
}

const Login = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const [resetEmail, setResetEmail] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);

  const [alert, setAlert] = useState({ message: "", type: "" });

  // const [debugAuth, setDebugAuth] = useState("");


  const { search } = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(search);
  const next = searchParams.get("next") || "/home";
  const reviewerAutoLogin = searchParams.get("reviewer") === "1";

  const showAlert = (message, type = "info") => {
    setAlert({ message, type });
    setTimeout(() => setAlert({ message: "", type: "" }), 5000);
  };

  // const pushDebug = (line) => {
  //   setDebugAuth((prev) => [line, prev].filter(Boolean).join("\n"));
  // };

  // ─────────────────────────────────────────────────────────────
  // Discord sign-in via ?token= (custom token)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const token = new URLSearchParams(search).get("token");
    if (!token) return;

    setLoading(true);
    signInWithCustomToken(auth, token)
      .then(() => {
        showAlert("Discord sign-in successful!", "success");
        navigate(next, { replace: true });
      })
      .catch((err) => {
        console.error("Discord custom-token error:", err);
        showAlert("Discord sign-in failed.", "error");
      })
      .finally(() => {
        setLoading(false);
        window.history.replaceState({}, "", window.location.pathname);
      });
  }, [search, navigate, next]);

  const signInWithEmailPasswordAndNavigate = useCallback(
    async ({
      email,
      password: passwordValue,
      successMessage = "Login successful!",
      showSuccessAlert = true,
      showUnverifiedAlert = true,
    }) => {
      await signInWithEmailAndPassword(auth, email, passwordValue);

      const user = auth.currentUser;
      if (user) {
        await user.reload();

        if (!user.emailVerified) {
          if (showUnverifiedAlert) {
            showAlert(
              "Login successful â€” your email isnâ€™t verified yet, so your access is limited until you verify.",
              "info"
            );
          }
          navigate(next, { replace: true });
          return;
        }

        try {
          const grant = httpsCallable(functions, "grantVerifiedAccess");
          await grant();
        } catch (e) {
          console.warn("grantVerifiedAccess failed or unavailable:", e);
        }
      }

      if (showSuccessAlert) {
        showAlert(successMessage, "success");
      }

      navigate(next, { replace: true });
    },
    [navigate, next]
  );

  useEffect(() => {
    if (!reviewerAutoLogin) return;

    let cancelled = false;

    const autoLogin = async () => {
      setLoading(true);

      try {
        handledPostLoginRef.current = true;

        if (
          auth.currentUser &&
          auth.currentUser.email?.toLowerCase() !== REVIEWER_EMAIL.toLowerCase()
        ) {
          await signOut(auth);
        }

        if (
          auth.currentUser &&
          auth.currentUser.email?.toLowerCase() === REVIEWER_EMAIL.toLowerCase()
        ) {
          navigate(next, { replace: true });
          return;
        }

        await signInWithEmailPasswordAndNavigate({
          email: REVIEWER_EMAIL,
          password: REVIEWER_PASSWORD,
          showSuccessAlert: false,
          showUnverifiedAlert: false,
        });
      } catch (error) {
        console.error("[Login] Reviewer auto-login failed:", error);
        handledPostLoginRef.current = false;

        if (!cancelled) {
          showAlert("Reviewer auto-login failed. Please try again.", "error");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    autoLogin();

    return () => {
      cancelled = true;
    };
  }, [navigate, next, reviewerAutoLogin, signInWithEmailPasswordAndNavigate]);

  // ─────────────────────────────────────────────────────────────
  // Post-login handling (Google popup / redirect / auth restore)
  // IMPORTANT: avoid Firestore write if /users/{uid} already exists
  // ─────────────────────────────────────────────────────────────
  const handledPostLoginRef = useRef(false);

  const handlePostLogin = useCallback(
    async (user) => {
      if (!user) return;
      if (handledPostLoginRef.current) return;
      handledPostLoginRef.current = true;

      setLoading(true);
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const isNewUser = !userSnap.exists();

        // ✅ Only create the user record if it DOESN'T exist
        if (!userSnap.exists()) {
          // 1) Create base user doc (no username yet, but DO mark verified for Google)
          await createUserRecord(user, {
            username: null,
            needsEmailVerification: false,
            emailVerified: true,
            theme: "dark",
            // leave specialCode to server-side applyPromoCode
          });

          // 2) Apply the EarlyRelease access code server-side
          try {
            const apply = httpsCallable(functions, "applyPromoCode");
            await apply({ code: "EarlyRelease" });
          } catch (e) {
            console.warn("applyPromoCode(EarlyRelease) failed:", e);
          }

          // 3) Assign a temporary username (kept if they never choose one)
          // Try a few times in case of collision
          let temp = null;
          for (let i = 0; i < 10; i++) {
            const candidate = makeTempUsername();
            const candidateLower = candidate.toLowerCase();
            const snap = await getDoc(doc(db, "usernames", candidateLower));
            if (!snap.exists()) {
              temp = candidate;
              break;
            }
          }

          if (temp) {
            await createUserRecord(user, { username: temp });
            await reserveUsername(db, user.uid, temp);
          }

          // 4) Optional: if you still want to set claims/etc
          try {
            const grant = httpsCallable(functions, "grantVerifiedAccess");
            await grant();
          } catch (e) {
            console.warn("grantVerifiedAccess failed or unavailable:", e);
          }
        }

        // Re-read after potential creation
        const finalSnap = userSnap.exists() ? userSnap : await getDoc(userRef);
        const data = finalSnap.data() || {};

        // ✅ NEW USERS: always go to PickUsername (even if a temp username was assigned)
        if (isNewUser) {
          navigate(`/pick-username?next=${encodeURIComponent(next)}`, { replace: true });
          return;
        }

        // ✅ Returning users
        if (data?.username) {
          showAlert("Login successful!", "success");
          navigate(next, { replace: true });
          return;
        }

        navigate(`/pick-username?next=${encodeURIComponent(next)}`, { replace: true });
      } catch (err) {
        console.error("Post-login handling failed:", err);
        showAlert(
          "Signed in, but profile setup was blocked. Please try again or contact support.",
          "error"
        );
        handledPostLoginRef.current = false;
      } finally {
        setLoading(false);
      }
    },
    [navigate, next]
  );

  useEffect(() => {
    console.log("[Login] Current URL after load:", window.location.href);
  }, []);

  console.log("Auth Domain:", process.env.REACT_APP_FIREBASE_AUTH_DOMAIN);

  // Handle Google redirect result (comes back after signInWithRedirect)

  // useEffect(() => {
  //   (async () => {
  //     try {
  //       pushDebug(`[redirect] checking getRedirectResult...`);
  //       const result = await getRedirectResult(auth);

  //       pushDebug(
  //         `[redirect] getRedirectResult: ${result?.user ? "HAS_USER" : result ? "HAS_RESULT_NO_USER" : "null"}`
  //       );

  //       if (result?.user) {
  //         pushDebug(`[redirect] user uid: ${result.user.uid}`);
  //         await handlePostLogin(result.user);
  //       }
  //     } catch (err) {
  //       const code = err?.code || "";
  //       const msg = err?.message || "";
  //       pushDebug(`[redirect] ERROR: ${code || msg || "Unknown"}`);
  //       showAlert(`Google redirect failed: ${code || msg || "Unknown error"}`, "error");
  //     }
  //   })();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // Fallback: on some mobile browsers redirect result can be null but auth restores
  useEffect(() => {
    if (reviewerAutoLogin) return undefined;

    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) handlePostLogin(user);
    });
    return () => unsub();
  }, [handlePostLogin, reviewerAutoLogin]);

  // useEffect(() => {
  //   const origin = window.location.origin;

  //   const unsub = onAuthStateChanged(auth, (user) => {
  //     setDebugAuth((prev) =>
  //       [
  //         `origin: ${origin}`,
  //         `user: ${user ? user.uid : "null"}`,
  //         prev,
  //       ].filter(Boolean).join("\n")
  //     );
  //   });

  //   const onUnhandled = (e) => {
  //     setDebugAuth((prev) =>
  //       `${prev}\nunhandledrejection: ${String(e.reason?.code || e.reason)}`
  //     );
  //   };

  //   window.addEventListener("unhandledrejection", onUnhandled);

  //   return () => {
  //     unsub();
  //     window.removeEventListener("unhandledrejection", onUnhandled);
  //   };
  // }, []);


  // ─────────────────────────────────────────────────────────────
  // Email/password + Username/password login
  // ─────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!identifier || !password) {
      showAlert("Please enter your username/email and password.", "error");
      return;
    }

    setLoading(true);

    try {
      // Email login
      if (identifier.includes("@")) {
        const email = identifier.trim();
        await signInWithEmailPasswordAndNavigate({
          email,
          password,
        });
        return;
      }

      // Username login via Cloud Run
      const uname = identifier.trim().toLowerCase();
      const resp = await fetch("https://loginwithusername-4huscqyw5q-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: uname, password }),
      });

      const data = await resp.json();

      if (!resp.ok || !data.token) {
        const code = data?.code || data?.error || "UNKNOWN";
        console.log("loginWithUsername failed:", { status: resp.status, data });

        if (code === "INVALID_PASSWORD") {
          showAlert("Incorrect password.", "error");
        } else if (code === "EMAIL_NOT_FOUND") {
          showAlert(
            "No email/password login exists for this account. Use Google/Discord, then add a password in Account.",
            "error"
          );
        } else if (code === "TOO_MANY_ATTEMPTS_TRY_LATER") {
          showAlert("Too many attempts. Try again later.", "error");
        } else {
          showAlert("Login failed. Please check your credentials and try again.", "error");
        }
        return;
      }

      await signInWithCustomToken(auth, data.token);
      showAlert("Login successful!", "success");
      navigate(next, { replace: true });
    } catch (error) {
      console.error("Login error:", error);

      try {
        const methods = identifier.includes("@")
          ? await fetchSignInMethodsForEmail(auth, identifier.trim())
          : [];

        if (methods.length && !methods.includes("password")) {
          showAlert(
            "This email doesn’t have a password yet. Log in with Google/Discord, then add a password in Account → 'Add Email & Password Login'.",
            "error"
          );
        } else {
          showAlert("Login failed. Please check your credentials and try again.", "error");
        }
      } catch (methodErr) {
        console.error(methodErr);
        showAlert("Login failed. Please check your credentials and try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Google sign-in (DEV = popup only, PROD = redirect on real mobile/in-app)
  // ─────────────────────────────────────────────────────────────
  const handleGoogleSignIn = async () => {
    console.log("[Login] handleGoogleSignIn start", {
      shouldUseRedirect: shouldUseRedirect(),
      mobileBrowser: isMobileBrowser(),
      inApp: isInAppBrowser(),
      maxTouchPoints: navigator.maxTouchPoints,
      ua: navigator.userAgent,
      href: window.location.href,
      nodeEnv: process.env.NODE_ENV,
    });

    setLoading(true);

    try {
      await setPersistence(auth, browserLocalPersistence);

      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      // ✅ Dev stability: never use redirect on localhost (DevTools mobile emulation is unreliable)
      if (isLocalhost) {
        const result = await signInWithPopup(auth, googleProvider);
        if (result?.user) await handlePostLogin(result.user);
        return;
      }

      // ✅ Mobile and in-app browsers: prefer redirect (popups often fail there)
      if (shouldUseRedirect()) {
        await signInWithRedirect(auth, googleProvider);
        return;
      }

      // ✅ Normal browsers (including iPhone Safari):
      // Try popup first; if Safari blocks it, fall back to redirect.
      try {
        const result = await signInWithPopup(auth, googleProvider);
        if (result?.user) await handlePostLogin(result.user);
        return;
      } catch (popupErr) {
        const popupCode = popupErr?.code || "";
        console.warn("[Login] Popup failed, falling back to redirect:", popupCode);

        // Mobile browsers can throw popup- or environment-related errors here.
        if (
          popupCode === "auth/popup-blocked" ||
          popupCode === "auth/popup-closed-by-user" ||
          popupCode === "auth/cancelled-popup-request" ||
          popupCode === "auth/operation-not-supported-in-this-environment"
        ) {
          await signInWithRedirect(auth, googleProvider);
          return;
        }

        // Anything else should bubble to the main catch
        throw popupErr;
      }
    } catch (error) {
      console.error("Google sign-in error (full):", error);
      const code = error?.code || "";
      const msg = error?.message || "";
      showAlert(`Google sign-in failed: ${code || msg || "Unknown error"}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      showAlert("Please enter your email to reset the password.", "error");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      showAlert("Password reset email sent! Check your inbox.", "success");
    } catch (error) {
      console.error("Password reset error:", error);
      showAlert("Failed to send reset email. Please try again.", "error");
    }
  };

  return (
    <div id="Login-Section">
      {loading && (
        <div className="login-loading-overlay" role="status" aria-live="polite">
          <div className="login-loading-card">
            <div className="login-spinner" />
            <p>Signing you in…</p>
            <p className="login-loading-sub">Just a moment while we set up your account.</p>
          </div>
        </div>
      )}

      {alert.message && (
        <CustomAlert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert({ message: "", type: "" })}
        />
      )}

      <div id="login-container">
        <form id="login-form" onSubmit={handleSubmit}>
          <h2>Login</h2>

          <input
            type="text"
            id="login-email"
            placeholder="Username or Email"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            required
          />

          <div className="password-container">
            <input
              type={passwordVisible ? "text" : "password"}
              id="login-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <button
              type="button"
              id="toggle-password"
              onClick={() => setPasswordVisible(!passwordVisible)}
              aria-label="Toggle password visibility"
            >
              <i className={`fa-solid ${passwordVisible ? "fa-eye-slash" : "fa-eye"}`} />
            </button>
          </div>

          <button type="submit" id="login-btn" disabled={loading}>
            {loading ? "Logging in..." : "Login"}
          </button>

          <button
            type="button"
            id="google-btn"
            onClick={() => {
              console.log("[Login] Google button clicked", {
                loading,
                ua: navigator.userAgent,
                maxTouchPoints: navigator.maxTouchPoints,
              });
              handleGoogleSignIn();
            }}
            disabled={loading}
          >
            Continue with Google
          </button>

          {!showResetPassword && (
            <p className="forgot-password-link" onClick={() => setShowResetPassword(true)}>
              Forgot Password?
            </p>
          )}

          {showResetPassword && (
            <div id="forgot-password">
              <input
                type="email"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
              <button type="button" onClick={handleForgotPassword}>
                Reset Password
              </button>
            </div>
          )}

          <p>
            Don&apos;t have an account? <br />
            <a href="/signup" className="signup-link">
              Sign Up by E-mail
            </a>
          </p>
          {/* <pre style={{ whiteSpace: "pre-wrap", fontSize: "12px", opacity: 0.8 }}>
            {debugAuth}
          </pre> */}
        </form>
      </div>
    </div>
  );
};

export default Login;
